import { mergeStyles, Stack } from "@fluentui/react";
import { useContext, useEffect, useState } from "react"
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { HttpHelper } from "../../../Core/Http/HttpHelper";
import { ISessionStateContext, SessionStateContext } from "../../../Core/State/SessionStateContext"
import { SystemCore } from "../../../Core/System/SystemCore";
import { SystemApp, SystemDataLoadingStatus, User } from "../../../Model/SystemModels";
import { ErrorDialog, IErrorDialogProps } from "../ErrorDialog/ErrorDialog";
import { Loader } from "../Loader/Loader";
import jwt from 'jwt-decode';
import i18next, { t } from "i18next";
import { ApplicationNavBar } from "../ApplicationNavBar/ApplicationNavBar";
import { MessageType, ToastService } from "../../../Core/Toast/ToastService";

const containerClassName = mergeStyles([{
    backgroundColor: 'white !important',
    paddingTop: 51,
    width: '100%',
    zIndex: 10,
}]);

export interface IApplicationContext {
    fullScreen: boolean;
    setFullScreen: (fullscreen: boolean) => void;
}

export function useApplicationContext() {
    return useOutletContext<IApplicationContext>();
}

export const ApplicationContainer: React.FC = () => {
    const [applicationContext, setApplicationContext] = useState<IApplicationContext>({} as IApplicationContext);
    const [fulllScreen, setFullScreen] = useState<boolean>(false);

    const [dataStatusText, setDataStatusText] = useState("");
    const [dataStatus, SetDataStatus] = useState(SystemDataLoadingStatus.ToLoad);   
    const [systemInterval, setSystemInterval] = useState<number>(0);

    const navigate = useNavigate();
    const location = useLocation();

    const [errorDialogProps, SetErrorDialogProps] = useState({} as IErrorDialogProps);

    let sessionStateContext: ISessionStateContext = useContext(SessionStateContext);

    const getRequestedResource = () => {
        let requestedResource = HttpHelper.getParameter("resource");

        if ((requestedResource !== null) && (requestedResource !== ""))
            return atob(requestedResource);

        return undefined;
    }

    const createInterval = (claims: any) => {
        let d = new Date(0);
        let today = new Date();
        d.setUTCSeconds(claims.exp);
        let exprireTime = (d.getTime() - today.getTime());
        
        return window.setTimeout(() => {
            let systemCore: SystemCore = new SystemCore();
            systemCore.UserSessionExpired(sessionStateContext, navigate)
        }, exprireTime);
    }

    const buildRedirectUrl = (path: string) : string => {
        if (window.location.pathname != "/app") {
            const searchParams = new URLSearchParams(window.location.search);
            const emailParam = searchParams.get('email');
            if (emailParam)
                searchParams.delete('email');
            return path += "?resource=" + btoa(window.location.pathname+"?"+searchParams.toString())+(emailParam ? "&email="+emailParam : "");
        }
        return path;
    }

    const initPageStatus = () => {
        sessionStateContext.currentApp = new SystemApp();
        sessionStateContext.currentUser = new User();
        setDataStatusText("");
        SetDataStatus(SystemDataLoadingStatus.ToLoad);
    };

    const onOpenChatbot = () => {
        let chatbotPath: string = sessionStateContext.currentApp.homepageUrl.substring(0, sessionStateContext.currentApp.homepageUrl.lastIndexOf("/")) + "/chatbot";
        navigate(chatbotPath);
    }

    useEffect(() => {
        setApplicationContext({...applicationContext, fullScreen: fulllScreen});
    }, [fulllScreen]);

    useEffect(() => {
        if (dataStatus == SystemDataLoadingStatus.ToLoad)
        {
            let systemCore: SystemCore = new SystemCore();
            SetDataStatus(SystemDataLoadingStatus.Loading);
            setDataStatusText(t('common:ApplicationContainer:dataStatusText1')!);

            setApplicationContext(
                {
                    fullScreen: fulllScreen,
                    setFullScreen: setFullScreen
                }
            );

            systemCore.getUserSessionInformation().then((sessionInfo) => {
                sessionStateContext.currentApp = sessionInfo.app;
                sessionStateContext.currentUser = sessionInfo.user;
                sessionStateContext.currentUserSettings = sessionInfo.userSettings;
                document.title = sessionStateContext.currentApp.title;
                SystemCore.setFavicons("data:image/png;base64,"+sessionStateContext.currentApp.appIcon);
                setDataStatusText(t('common:ApplicationContainer:dataStatusText2')!);

                systemCore.getUserFeatures().then((features) => {
                    sessionStateContext.entries = features;
            
                    if(sessionInfo.userSettings.userLanguageCode !== systemCore.formatLanguage(i18next.language))
                        systemCore.setUserLanguageCode(sessionInfo.userSettings).then(()=>{
                            sessionStateContext.currentUserSettings.userLanguageCode = systemCore.formatLanguage(i18next.language);
                        });

                    SetDataStatus(SystemDataLoadingStatus.Loaded);

                    let token = systemCore.getUserToken();
                    let claims = jwt(token);
                    setSystemInterval(createInterval(claims));

                    let redirectUrl = getRequestedResource();
                    if (redirectUrl === undefined)
                    {
                        if (window.location.pathname == "/app")
                            navigate(sessionStateContext.currentApp.homepageUrl);
                    }
                    else
                        navigate(redirectUrl);
                })
                .catch((err) => {

                });
            })
            .catch((resp) => {
                console.error(resp.message);
                // ToastService.showMessage(MessageType.Error, t('common:ApplicationContainer:errorDialogText')!)
                systemCore.logOutUser(sessionStateContext, navigate);
            });
        }
    }, [dataStatus]);

    useEffect(() => {
        if (dataStatus == SystemDataLoadingStatus.Loaded)
        {
            if (location.pathname == "/app")
            {
                document.title = sessionStateContext.currentApp.title;
                navigate(sessionStateContext.currentApp.homepageUrl);
            }
        }
    }, [location.pathname]);

    if (sessionStateContext.isAuthenticated)
    {
        if (! SystemCore.isFinishedDataStatus(dataStatus))
            return <Loader text={dataStatusText} />
        else
        {
            if (dataStatus == SystemDataLoadingStatus.Error)
            {
                return <ErrorDialog showDialog={errorDialogProps.showDialog}
                                    text={errorDialogProps.text}
                                    title={errorDialogProps.title}
                                    onUpdateClick={errorDialogProps.onUpdateClick}/>;
            }
            else
            {
                return (
                    <>
                        {
                            applicationContext.fullScreen ?
                                <Outlet context={applicationContext}></Outlet>
                            :
                                <>
                                    <ApplicationNavBar
                                        onOpenChatbot={()=> onOpenChatbot()}
                                    />
                                    <Stack className={containerClassName}>
                                        <Outlet context={applicationContext}></Outlet>
                                    </Stack>
                                </>
                        }
                    </>
                );
            }
        }
    }
    else
        return <Navigate to={buildRedirectUrl("/login")} />;

}